<template>
  <section class="closing" :aria-labelledby="headingId">
    <h2 :id="headingId" class="closing__title">{{ title || t('cta.title') }}</h2>
    <p class="closing__lead">{{ lead || t('cta.lead') }}</p>
    <div class="closing__actions">
      <LandingButton variant="accent" :to="ctaTarget">{{ ctaLabel }}</LandingButton>
      <LandingButton v-if="!hideSecondary" variant="ghost" :to="{ name: 'Community' }">
        {{ t('community.joinDiscord') }}
      </LandingButton>
    </div>
  </section>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import LandingButton from '@/components/btn/LandingButton.vue';
import { usePrimaryCta } from '@/composables/usePrimaryCta.js';
import { nextUid } from '@/utils/uid';

/*
 * The closing action for a reading page.
 *
 * The gallery, the FAQ and the character preview each ended with nothing —
 * a visitor who read to the bottom had no offered next step, and on the FAQ
 * the only onward links in the whole page were buried inside one collapsed
 * accordion answer. Those are the pages people land on from search, so they
 * were the pages most likely to be someone's only visit.
 *
 * One accent button per page, so this must not be used on a page that already
 * has one.
 */
defineProps({
  title: { type: String, default: '' },
  lead: { type: String, default: '' },
  hideSecondary: Boolean,
});

const { t } = useI18n();
const { ctaTarget, ctaLabel } = usePrimaryCta();
const headingId = nextUid('closing');
</script>

<style scoped>
.closing {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  padding-top: var(--rhythm-block);
  border-top: 1px solid var(--border);
  text-align: center;
}

.closing__title {
  font-family: var(--font-display);
  font-size: var(--text-2xl);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-display);
  line-height: var(--leading-tight);
  color: var(--text);
  margin: 0;
  text-wrap: balance;
}

.closing__lead {
  color: var(--text-muted);
  max-width: var(--measure-narrow);
  margin: 0;
  text-wrap: pretty;
}

.closing__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-3);
  margin-top: var(--space-3);
}
</style>
